// src/oura/components/PageIdentity.tsx
//
// THE one way a page is presented anywhere in Oura: its name on top
// (text-sm font-medium), the path beneath as a link — clickable when the
// scheme is safe, plain text when it is not. Extracted from BusinessOverview's
// PageCell so every surface that lists pages (the Pages tab, the Overview
// cards, the audit drawers) renders them identically instead of each inventing
// its own alignment and truncation.
//
// TWO RULES, both learned from the Pages table.
//
// THE NAME WRAPS, IT DOES NOT TRUNCATE. A page's name is the thing the reader
// is scanning for, and real titles are long: "AIRFLOW Spa Ultra-Comfort – Blanc
// Dental Clinic | Perfect…" cut off at the ellipsis is the same string on every
// row of a site whose titles all end in the practice name, so the column that
// exists to tell rows apart stopped telling them apart. Wrapping costs a line
// of height and returns the distinguishing words.
//
// THE SECOND LINE IS THE PATH, NOT THE FULL URL. Every page in a workspace is
// on the same domain, so "https://blancdental.com.my/" was ~28 identical
// characters in front of the only part that differs — and it was pushing that
// part into an ellipsis. The full URL is still what the link points at and
// still what the tooltip shows; it just is not what gets rendered. (This
// reverses the module's original "always show the full URL" rule, which was
// written for consistency's sake before there was a table wide enough to show
// what it cost.)

import type { ReactNode } from "react";
import { ExternalLinkIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { pageHref, pagePath } from "../lib/oura";

export function PageIdentity({
    title,
    url,
    className,
    nameSuffix,
}: {
    /** The page's own title. Falls back to the path when the crawler found
     *  none — never the raw URL twice, once bold and once blue. */
    title?: string;
    url: string;
    className?: string;
    /**
     * A marker that belongs to the page itself, e.g. "Chosen by Oura".
     *
     * Rendered on the PATH line, beside the page's address. It used to sit on
     * the name line, which worked while the name was one truncated line and
     * stopped working the moment the name could wrap: as a flex sibling it took
     * its share of the width, so the title broke early and the badge landed in
     * the gap — reading as though "Chosen by Oura" were part of the title,
     * between its first and second lines.
     */
    nameSuffix?: ReactNode;
}) {
    const href = pageHref(url);
    const path = pagePath(url);
    // A missing or URL-identical "title" renders the path as the name, so the
    // two lines never show the same string in two styles.
    const hasName = !!title && title !== url;
    const name = hasName ? title : path;

    // The path is the whole identity of an untitled page, so printing it above
    // itself would be the exact duplication this component exists to avoid.
    // One line, and it is the link.
    const link = (
        <span className="inline-flex min-w-0 max-w-full items-center gap-1" title={url}>
            <span className="truncate">{path}</span>
            <ExternalLinkIcon className="h-3 w-3 flex-shrink-0" />
        </span>
    );

    return (
        <div className={cn("min-w-0", className)}>
            {hasName && (
                // The name gets the full width to itself. Nothing shares this
                // line, so it wraps where the words run out rather than where a
                // neighbouring element pushed it.
                <div className="break-words text-sm font-medium text-gray-900">{name}</div>
            )}
            <div className={cn("flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5", hasName && "mt-0.5")}>
                {href ? (
                    <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className={cn(
                            "min-w-0 hover:underline",
                            // An untitled page's path IS its name, so it carries
                            // the name's weight and sits alone on the row.
                            hasName
                                ? "text-xs text-blue-600"
                                : "text-sm font-medium text-blue-600",
                        )}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {link}
                    </a>
                ) : (
                    // Unsafe scheme off the crawler: shown, never clickable.
                    <span
                        className={cn(
                            "min-w-0",
                            hasName ? "text-xs text-gray-500" : "text-sm font-medium text-gray-700",
                        )}
                    >
                        {link}
                    </span>
                )}
                {nameSuffix}
            </div>
        </div>
    );
}
